import { Component, inject } from "@angular/core";
import {
    ZipProgress,
    ZipDownloadService,
} from "../../services/zip-download.service";
import { Helpers } from "../../utils/browser";
import { NotificationService } from "../../services/notification.service";
import { DomSanitizer } from "@angular/platform-browser";

@Component({
    selector: "zbd-zip-download",
    templateUrl: "zip-download.component.html",
    styleUrls: ["./zurich-style.css", "./zurich.css", "./main.css"],
    styles: [
        `
            .leader {
                font-weight: bold;
            }

            .waiting {
                padding-top: 0.3em;
                text-align: center;
                font-size: 250%;
            }

            .content {
                background: white;
                height: 20px;
                color: black;
                display: table;
            }

            .saved {
                color: #bfbfbf;
            }
        `,
    ],
})
export class ZipDownloadComponent {
    isVisible = false;
    state: State = State.STOPPED;
    stopRequested = false;

    zipFileIterator: AsyncIterableIterator<ZipProgress> = null;

    downloadedFiles: string[] = [];
    downloadProgress: ZipProgress = null;

    private sanitizer = inject(DomSanitizer);

    constructor(
        private zipService: ZipDownloadService,
        private notify: NotificationService,
    ) {
        if (localStorage.getItem(LOCAL_STORAGE_KEY) !== null) {
            this.state = State.INTERRUPTED;
        }
    }

    open() {
        this.isVisible = true;
    }

    close() {
        this.isVisible = false;
        if (this.isDownloading) {
            this.requestStop();
        }
    }

    startDownload() {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        this.downloadedFiles = [];
        this.zipFileIterator = this.zipService.fetchZipFiles();
        this.downloadNext();
    }

    continueDownload() {
        const { page, processId } = JSON.parse(
            localStorage.getItem(LOCAL_STORAGE_KEY),
        );
        this.zipFileIterator = this.zipService.fetchZipFiles(
            processId + 1,
            page,
        );
        this.downloadNext();
    }

    async downloadNext() {
        this.state = State.DOWNLOADING;
        try {
            const { value, done } = this.stopRequested
                ? await this.zipFileIterator.return()
                : await this.zipFileIterator.next();

            if (done) {
                if (this.stopRequested) {
                    this.stopRequested = false;
                    this.state = State.INTERRUPTED;
                } else {
                    localStorage.removeItem(LOCAL_STORAGE_KEY);
                    this.state = State.STOPPED;
                }
                this.downloadProgress = null;
                this.zipFileIterator = null;
            } else {
                this.downloadProgress = value;
                if (value.data) {
                    this.state = State.PAUSED;
                } else {
                    this.downloadNext();
                }
            }
        } catch (error) {
            if (this.downloadProgress === null) {
                this.state = State.STOPPED;
            } else {
                this.state = State.INTERRUPTED;
            }
            this.downloadProgress = null;
            this.notify.errorExt(
                "Zip-Download",
                'Die Erstellung der Zip-Datei wurde kurzzeitig unterbrochen. Der aktuelle Block der heruntergeladenen PDFs wurde für Sie in der Zip-Datei gebündelt und wird Ihnen im aktuellen Dialog angezeigt. Bitte klicken Sie auf "speichern" und anschließend auf "Download fortsetzen". Technische Fehlerdetails:',
                error,
            );
        }
    }

    async saveFile() {
        await Helpers.downloadBlob(
            () => ({
                fileName: this.fileName,
                data: this.downloadProgress.data,
            }),
            (filename, blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a") as HTMLAnchorElement;
                link.href = url;
                link.download = filename;
                link.click();
                URL.revokeObjectURL(url);
                link.remove();
            },
        );
        this.downloadProgress.data = undefined;
        this.downloadedFiles.push(this.fileName);
        if (this.isPaused) {
            this.downloadNext();
        }
        localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify({
                page: this.downloadProgress.page,
                processId: this.downloadProgress.lastProcessId,
            }),
        );
    }

    requestStop() {
        this.stopRequested = true;
    }

    get fileName() {
        if (this.downloadProgress !== null) {
            const first = this.downloadProgress.firstProcessId;
            const last = this.downloadProgress.lastProcessId;
            if (first === last) {
                return `Beratungsprotokoll_${first}.zip`;
            } else {
                return `Beratungsprotokolle_${first}_bis_${last}.zip`;
            }
        }
    }

    get hasUnsavedFile() {
        return this.downloadProgress !== null && !!this.downloadProgress.data;
    }

    get isStopped() {
        return this.state === State.STOPPED;
    }

    get isDownloading() {
        return this.state === State.DOWNLOADING;
    }

    get isPaused() {
        return this.state === State.PAUSED;
    }

    get isInterrupted() {
        return this.state === State.INTERRUPTED;
    }
}

const LOCAL_STORAGE_KEY = "zip-download-progress";

enum State {
    STOPPED,
    DOWNLOADING,
    PAUSED,
    INTERRUPTED,
}
